import type {
  CreateHierarchicalRulesetRequest,
  CreateHierarchicalRulesetResponse,
  GetAllowedRuleTypesResponse,
  GetAllowedRulesResponse,
  GetHierarchicalRulesetResponse,
  GetHierarchicalRulesetsResponse,
  HierarchicalRuleset,
  LegacyRulesets,
  LegacyRulesetsRequest,
  LegacyRulesetsResponse,
  PreviewChangesIndividualRuleset,
  PreviewChangesResponse,
  UpdateHierarchicalRulesetRequest,
  UpdateHierarchicalRulesetResponse
} from '@library/types/rulesets';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';

const { admin } = apiPaths;

export const rulesetsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getHierarchicalRulesets: build.query<
      GetHierarchicalRulesetsResponse,
      {
        hierarchyType: string;
        rootLevelScopeIds?: string[];
        expandAllPhantoms?: boolean;
        includePhantomRoot?: boolean;
      }
    >({
      query: ({
        hierarchyType,
        rootLevelScopeIds,
        expandAllPhantoms,
        includePhantomRoot
      }) => ({
        url: `${admin}hierarchical_rulesets/by_hierarchy_type/${hierarchyType}`,
        method: 'GET',
        params: {
          root_level_scope_ids: rootLevelScopeIds?.join(','),
          expand_all_phantoms: expandAllPhantoms,
          include_phantom_root: includePhantomRoot
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((item) => ({
                type: 'Rulesets' as const,
                id: item.attributes?.qualified_path || item.id
              })),
              'Rulesets'
            ]
          : ['Rulesets']
    }),
    createHierarchicalRuleset: build.mutation<
      HierarchicalRuleset,
      CreateHierarchicalRulesetRequest
    >({
      query: (payload) => ({
        url: `${admin}hierarchical_rulesets`,
        method: 'POST',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: CreateHierarchicalRulesetResponse) =>
        response.data,
      invalidatesTags: (result) => [
        {
          type: 'Rulesets',
          id: result?.attributes.qualified_path || result?.id
        }
      ]
    }),
    getHierarchicalRuleset: build.query<
      HierarchicalRuleset,
      {
        id?: string;
        qualified_path?: string;
        expand_all_phantoms?: boolean;
        include_phantom_root?: boolean;
      }
    >({
      query: ({ id, ...params }) => ({
        url: `${admin}hierarchical_rulesets${id ? `/${id}` : ''}`,
        method: 'GET',
        params,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: GetHierarchicalRulesetResponse) =>
        Array.isArray(response.data) ? response.data[0] : response.data,
      providesTags: (_result, _error, arg) => [
        { type: 'Rulesets', id: arg.qualified_path || arg.id }
      ]
    }),
    updateHierarchicalRuleset: build.mutation<
      HierarchicalRuleset,
      { id: string } & UpdateHierarchicalRulesetRequest
    >({
      query: ({ id, ...payload }) => ({
        url: `${admin}hierarchical_rulesets/${id}`,
        method: 'PATCH',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: UpdateHierarchicalRulesetResponse) =>
        response.data,
      invalidatesTags: (result, _error, { id }) => [
        {
          type: 'Rulesets',
          id: result?.attributes.qualified_path || id
        }
      ]
    }),
    deleteHierarchicalRuleset: build.mutation<
      HierarchicalRuleset,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${admin}hierarchical_rulesets/${id}`,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      invalidatesTags: (result, _error, { id }) => [
        {
          type: 'Rulesets',
          id: result?.attributes.qualified_path || id
        }
      ]
    }),
    getAllowedRules: build.query<GetAllowedRulesResponse, void>({
      query: () => ({
        url: `${admin}hierarchical_rulesets/allowed_rules`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
    }),
    getAllowedRuleTypes: build.query<GetAllowedRuleTypesResponse, void>({
      query: () => ({
        url: `${admin}hierarchical_rulesets/allowed_rule_types`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
    }),
    previewChanges: build.mutation<
      PreviewChangesIndividualRuleset,
      { id: string } & UpdateHierarchicalRulesetRequest
    >({
      query: ({ id, ...payload }) => ({
        url: `${admin}hierarchical_rulesets/${id}/preview`,
        method: 'POST',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: PreviewChangesResponse) => response.data
    }),
    getLegacyRulesets: build.query<LegacyRulesets, LegacyRulesetsRequest>({
      query: ({ omit_users, page, per_page }) => ({
        url: `${admin}rulesets`,
        method: 'GET',
        params: {
          omit_users,
          page,
          per_page
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: LegacyRulesetsResponse) => response.data,
      providesTags: ['LegacyRulesets']
    })
  })
});

export const {
  useGetHierarchicalRulesetsQuery,
  useCreateHierarchicalRulesetMutation,
  useGetHierarchicalRulesetQuery,
  useUpdateHierarchicalRulesetMutation,
  useDeleteHierarchicalRulesetMutation,
  useGetAllowedRulesQuery,
  useGetAllowedRuleTypesQuery,
  usePreviewChangesMutation,
  useGetLegacyRulesetsQuery
} = rulesetsApi;
